import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  Download, Loader2, Shuffle, ImageOff,
  Heart, MessageCircle, Send, Bookmark, MoreHorizontal, X, ChevronUp, ChevronRight,
} from 'lucide-react';

const API = 'https://api.glowytics.com';
const LOGO_URL = `${API}/pixel/yatas-bedding-logo-beyaz.png`;
const proxied = (url) => `${API}/api/yatas-frame-studio/image-proxy?url=${encodeURIComponent(url)}`;

// Canvas çıktı boyutu (4:5 sosyal medya dikey)
const W = 1080;
const H = 1350;

const trMoney = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });
const fmtPrice = (v, cur) => {
  if (v == null || isNaN(v)) return '';
  const sym = cur === 'TRY' || cur === 'TL' ? '₺' : (cur ? cur + ' ' : '');
  return sym + trMoney.format(Math.round(v));
};

// ---------------------------------------------------------------------------
// Canvas yardımcıları
// ---------------------------------------------------------------------------
function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// Görseli "contain" ile verilen kutuya oturt (oranı bozmadan)
function drawContain(ctx, img, x, y, w, h) {
  const ir = img.width / img.height;
  const br = w / h;
  let dw = w, dh = h;
  if (ir > br) { dh = w / ir; } else { dw = h * ir; }
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

// Görseli "cover" ile kutuya oturt (kutuyu doldur, taşanı kırp)
function drawCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height;
  const br = w / h;
  let sw = img.width, sh = img.height, sx = 0, sy = 0;
  if (ir > br) { sw = img.height * br; sx = (img.width - sw) / 2; }
  else { sh = img.width / br; sy = (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

// Görseli CORS-safe yükle (canvas taint olmasın diye crossOrigin anonymous)
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('img yüklenemedi: ' + src));
    im.src = src;
  });
}

// Beyaz logoyu, verilen genişliğe göre (oran koruyarak) çiz. Ortalanır.
function drawLogo(ctx, logo, cx, cy, maxW) {
  if (!logo) return;
  const ratio = logo.width / logo.height;
  const w = maxW;
  const h = w / ratio;
  ctx.drawImage(logo, cx - w / 2, cy - h / 2, w, h);
}

// Metni kelime kelime saran yardımcı → satır dizisi
function wrapLines(ctx, text, maxW) {
  const words = String(text || '').split(/\s+/);
  const lines = [];
  let line = '';
  for (const word of words) {
    const test = line ? line + ' ' + word : word;
    if (ctx.measureText(test).width > maxW && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// ---------------------------------------------------------------------------
// "Kart" stili şablon fabrikası (Lacivert Degrade tarzı):
//   degrade zemin + beyaz yuvarlak kart + ürün + isim/fiyat + alt logo.
//   grad: [renk1, renk2]  ·  accent: indirimsiz fiyat rengi  ·  topText: üst başlık (opsiyonel)
// ---------------------------------------------------------------------------
function makeCardRender({ grad, accent = '#12263a', topText = null, badge = null }) {
  return (ctx, { img, logo, product }) => {
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, grad[0]);
    g.addColorStop(1, grad[1]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // opsiyonel üst başlık (kampanya) — kartın üst kenarına yaslı, büyük punto
    const topZone = topText ? 150 : 0;
    const cardTop = 120 + topZone;
    if (topText) {
      const tlines = Array.isArray(topText) ? topText : [topText];
      const defs = tlines.map((t, i) => {
        const big = i === tlines.length - 1;
        return { t, size: big ? 68 : 48, weight: big ? 800 : 600, color: big ? '#ffe8a3' : '#ffffff' };
      });
      const gap = 10;
      const blockH = defs.reduce((a, d) => a + d.size, 0) + gap * (defs.length - 1);
      let y = cardTop - 26 - blockH; // bloğun altı kart üst kenarının ~26px üstünde bitsin
      ctx.textAlign = 'center';
      for (const d of defs) {
        y += d.size; // baseline
        ctx.font = `${d.weight} ${d.size}px Poppins, Arial, sans-serif`;
        ctx.fillStyle = d.color;
        ctx.fillText(d.t, W / 2, y);
        y += gap;
      }
      ctx.textAlign = 'left';
    }

    // beyaz yuvarlak kart + gölge
    const cx = 110, cy = cardTop, cw = W - 220, ch = H - 320 - topZone;
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 20;
    ctx.fillStyle = '#ffffff';
    roundRect(ctx, cx, cy, cw, ch, 40);
    ctx.fill();
    ctx.restore();

    // kart içi: üstte ürün (kırpma), altta isim + fiyat
    const textZone = 170;
    ctx.save();
    roundRect(ctx, cx, cy, cw, ch, 40);
    ctx.clip();
    drawContain(ctx, img, cx + 40, cy + 40, cw - 80, ch - 80 - textZone);
    ctx.restore();

    // ince ayraç çizgisi
    const tx = cx + 50;
    const sepY = cy + ch - textZone + 8;
    ctx.strokeStyle = '#ececec';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(tx, sepY);
    ctx.lineTo(cx + cw - 50, sepY);
    ctx.stroke();

    // ürün ismi (koyu, en fazla 2 satır — sağdaki fiyata yer bırakır)
    ctx.textAlign = 'left';
    ctx.fillStyle = '#1f2937';
    ctx.font = '600 34px Poppins, Arial, sans-serif';
    let ty = sepY + 46;
    const lines = wrapLines(ctx, product.title, cw - 330).slice(0, 2);
    for (const ln of lines) { ctx.fillText(ln, tx, ty); ty += 42; }

    // fiyat — sağ alt köşe, sağa yaslı
    if (product.price != null) {
      const hasDiscount = product.salePrice != null && product.salePrice < product.price;
      const rx = cx + cw - 50;
      const py = cy + ch - 36;
      ctx.textAlign = 'right';
      if (hasDiscount) {
        ctx.font = '500 30px Poppins, Arial, sans-serif';
        ctx.fillStyle = '#9ca3af';
        const oldTxt = fmtPrice(product.price, product.priceCurrency);
        const oldY = py - 48;
        ctx.fillText(oldTxt, rx, oldY);
        const ow = ctx.measureText(oldTxt).width;
        ctx.strokeStyle = '#9ca3af';
        ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.moveTo(rx - ow, oldY - 10); ctx.lineTo(rx, oldY - 10); ctx.stroke();
        ctx.font = '800 56px Poppins, Arial, sans-serif';
        ctx.fillStyle = '#c0392b';
        ctx.fillText(fmtPrice(product.salePrice, product.salePriceCurrency), rx, py);
      } else {
        ctx.font = '700 48px Poppins, Arial, sans-serif';
        ctx.fillStyle = accent;
        ctx.fillText(fmtPrice(product.price, product.priceCurrency), rx, py);
      }
      ctx.textAlign = 'left';
    }

    drawLogo(ctx, logo, W / 2, H - 110, 440);

    // opsiyonel yuvarlak rozet (ör. vade farksız taksit) — sağ üst, hafif eğik
    if (badge) {
      ctx.save();
      ctx.translate(badge.cx, badge.cy);
      if (badge.rotate) ctx.rotate((badge.rotate * Math.PI) / 180);
      // daire + gölge
      ctx.shadowColor = 'rgba(0,0,0,0.30)';
      ctx.shadowBlur = 26;
      ctx.shadowOffsetY = 8;
      ctx.fillStyle = badge.bg;
      ctx.beginPath();
      ctx.arc(0, 0, badge.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowColor = 'transparent';
      // iç halka
      ctx.strokeStyle = badge.ring || 'rgba(0,0,0,0.18)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(0, 0, badge.r - 13, 0, Math.PI * 2);
      ctx.stroke();
      // metin satırları (dikeyde ortalı)
      ctx.fillStyle = badge.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const totalH =
        badge.lines.reduce((a, l) => a + l.size, 0) + 6 * (badge.lines.length - 1);
      let yy = -totalH / 2;
      for (const l of badge.lines) {
        yy += l.size / 2;
        ctx.font = `${l.weight} ${l.size}px Poppins, Arial, sans-serif`;
        ctx.fillText(l.t, 0, yy);
        yy += l.size / 2 + 6;
      }
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'left';
      ctx.restore();
    }
  };
}

// ---------------------------------------------------------------------------
// Çerçeve tanımları — her biri canvas'a çizer
//   render(ctx, { img, logo, product })
//   filter (opsiyonel): backend'den bu kategoriye göre ürün çeker
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// "Kolaj" şablonu: 1 büyük görsel solda + 2 küçük görsel sağda alt alta.
//   Ürünün item_group varyant görsellerinden gelir (imgs dizisi).
//   Üstte logo, altta "%.. VARAN İNDİRİM" yazısı.
// ---------------------------------------------------------------------------
function makeCollageRender({ bg = '#0c0c0c', promoText = { pre: "%50'YE VARAN", hi: 'İNDİRİM' } }) {
  return (ctx, { imgs, logo }) => {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // üstte logo
    drawLogo(ctx, logo, W / 2, 150, 470);

    // görsel bölgesi
    const m = 60, gap = 22;
    const top = 250, bottom = 1170;
    const regionH = bottom - top;
    const contentW = W - m * 2;
    const mainW = Math.round((contentW - gap) * 0.635);
    const rightW = contentW - gap - mainW;
    const cellH = Math.round((regionH - gap) / 2);

    const pics = [imgs[0], imgs[1] || imgs[0], imgs[2] || imgs[1] || imgs[0]];

    const cell = (x, y, w, h, im) => {
      ctx.fillStyle = '#f4f4f4';
      roundRect(ctx, x, y, w, h, 10);
      ctx.fill();
      ctx.save();
      roundRect(ctx, x, y, w, h, 10);
      ctx.clip();
      drawContain(ctx, im, x + 14, y + 14, w - 28, h - 28);
      ctx.restore();
    };

    cell(m, top, mainW, regionH, pics[0]);
    const rx = m + mainW + gap;
    cell(rx, top, rightW, cellH, pics[1]);
    cell(rx, top + cellH + gap, rightW, cellH, pics[2]);

    // alt promo yazı: beyaz "%.. VARAN " + kırmızı "İNDİRİM"
    const py = 1258;
    ctx.textBaseline = 'alphabetic';
    ctx.font = '800 60px Poppins, Arial, sans-serif';
    const pre = promoText.pre + ' ';
    const hi = promoText.hi;
    const wPre = ctx.measureText(pre).width;
    const wHi = ctx.measureText(hi).width;
    const startX = (W - (wPre + wHi)) / 2;
    ctx.textAlign = 'left';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(pre, startX, py);
    ctx.fillStyle = '#e11d2a';
    ctx.fillText(hi, startX + wPre, py);
  };
}

// ---------------------------------------------------------------------------
// "Split Kampanya" şablonu: sol renkli panel (isim + fiyat + %X kampanya),
//   sağda 2 kare görsel alt alta. Görseller item_group varyantlarından gelir.
// ---------------------------------------------------------------------------
function makeSplitPromoRender({
  grad,
  pct = 50,
  line1 = '2. Ürüne Sepette Ekstra',
  suffix = 'İndirim',
}) {
  return (ctx, { imgs, product, logo }) => {
    const leftW = 500;
    const panelCx = leftW / 2;

    // sağ: iki görsel alt alta, tüm yüksekliği kaplar (üstte/altta boşluk yok)
    const S = W - leftW; // 580 (genişlik)
    const gap = 16;
    const cellH = (H - gap) / 2;
    const pics = [imgs[0], imgs[1] || imgs[0]];
    const drawCell = (x, y, w, h, im) => {
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      ctx.fillStyle = '#efeae4';
      ctx.fillRect(x, y, w, h);
      drawCover(ctx, im, x, y, w, h);
      ctx.restore();
    };
    drawCell(leftW, 0, S, cellH, pics[0]);
    drawCell(leftW, cellH + gap, S, cellH, pics[1]);

    // sol panel degrade
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, grad[0]);
    g.addColorStop(1, grad[1]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, leftW, H);

    ctx.textAlign = 'center';
    const maxW = leftW - 84;

    // ürün ismi (üstte, sönük beyaz)
    ctx.font = '500 27px Poppins, Arial, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    let ny = 150;
    for (const ln of wrapLines(ctx, product.title, maxW).slice(0, 2)) {
      ctx.fillText(ln, panelCx, ny);
      ny += 34;
    }

    // fiyat (üstü çizili eski + büyük güncel)
    const hasDiscount =
      product.salePrice != null && product.price != null && product.salePrice < product.price;
    let priceY = 300;
    if (hasDiscount) {
      ctx.font = '600 34px Poppins, Arial, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      const oldTxt = fmtPrice(product.price, product.priceCurrency);
      ctx.fillText(oldTxt, panelCx, priceY);
      const ow = ctx.measureText(oldTxt).width;
      ctx.strokeStyle = 'rgba(255,255,255,0.55)';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(panelCx - ow / 2, priceY - 11);
      ctx.lineTo(panelCx + ow / 2, priceY - 11);
      ctx.stroke();
      priceY += 58;
      ctx.font = '800 58px Poppins, Arial, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(fmtPrice(product.salePrice, product.salePriceCurrency), panelCx, priceY);
    } else if (product.price != null) {
      ctx.font = '800 58px Poppins, Arial, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(fmtPrice(product.price, product.priceCurrency), panelCx, priceY);
    }

    // +
    ctx.fillStyle = '#ffffff';
    ctx.font = '800 76px Poppins, Arial, sans-serif';
    ctx.fillText('+', panelCx, 470);

    // kampanya satırı
    ctx.font = '700 44px Poppins, Arial, sans-serif';
    let cy = 590;
    for (const ln of wrapLines(ctx, line1, maxW)) {
      ctx.fillText(ln, panelCx, cy);
      cy += 52;
    }

    // dev "%50"
    const bigY = 850;
    ctx.font = '800 150px Poppins, Arial, sans-serif';
    const num = String(pct);
    const wNum = ctx.measureText(num).width;
    ctx.font = '800 48px Poppins, Arial, sans-serif';
    const wPct = ctx.measureText('%').width;
    const gx = panelCx - (wPct + 8 + wNum) / 2;
    ctx.textAlign = 'left';
    ctx.fillText('%', gx, bigY - 88);
    ctx.font = '800 150px Poppins, Arial, sans-serif';
    ctx.fillText(num, gx + wPct + 8, bigY);
    ctx.textAlign = 'center';

    // İndirim
    ctx.font = '700 62px Poppins, Arial, sans-serif';
    ctx.fillText(suffix, panelCx, 960);

    ctx.textAlign = 'left';

    // sol panel altında Yataş Bedding logosu
    drawLogo(ctx, logo, panelCx, H - 110, 340);
  };
}

const FRAMES = [
  {
    id: 'classic-gray',
    name: 'Klasik Gri',
    desc: 'Düz gri zemin · beyaz logo',
    swatch: '#5e5e5e',
    render(ctx, { img, logo }) {
      ctx.fillStyle = '#5e5e5e';
      ctx.fillRect(0, 0, W, H);
      // ürün ortada, alt logoya yer bırak
      drawContain(ctx, img, 90, 80, W - 180, H - 250);
      drawLogo(ctx, logo, W / 2, H - 100, 440);
    },
  },
  {
    id: 'minimal-white',
    name: 'Minimal Beyaz',
    desc: 'Beyaz zemin · ince çerçeve · koyu alt bant',
    swatch: '#ffffff',
    render(ctx, { img, logo }) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, W, H);
      // ince iç çerçeve
      ctx.strokeStyle = '#e5e0d8';
      ctx.lineWidth = 3;
      ctx.strokeRect(40, 40, W - 80, H - 80);
      // ürün
      drawContain(ctx, img, 90, 90, W - 180, H - 230);
      // koyu alt bant + logo
      ctx.fillStyle = '#3a3a3a';
      ctx.fillRect(40, H - 150, W - 80, 110);
      drawLogo(ctx, logo, W / 2, H - 95, 400);
    },
  },
  {
    id: 'navy-gradient',
    name: 'Lacivert Degrade',
    desc: 'Degrade zemin · beyaz kart · isim + fiyat',
    swatch: 'linear-gradient(135deg,#12263a,#3a6073)',
    render: makeCardRender({ grad: ['#12263a', '#3a6073'], accent: '#12263a' }),
  },
  {
    id: 'indigo-gradient',
    name: 'İndigo Degrade',
    desc: 'İndigo degrade · beyaz kart · isim + fiyat',
    swatch: 'linear-gradient(135deg,#1e1b4b,#4f46e5)',
    render: makeCardRender({ grad: ['#1e1b4b', '#4f46e5'], accent: '#1e1b4b' }),
  },
  {
    id: 'emerald-gradient',
    name: 'Zümrüt Degrade',
    desc: 'Yeşil degrade · beyaz kart · isim + fiyat',
    swatch: 'linear-gradient(135deg,#0b3d2e,#1f6f54)',
    render: makeCardRender({ grad: ['#0b3d2e', '#1f6f54'], accent: '#0b3d2e' }),
  },
  {
    id: 'wine-gradient',
    name: 'Bordo Degrade',
    desc: 'Şarabi degrade · beyaz kart · isim + fiyat',
    swatch: 'linear-gradient(135deg,#4a1220,#8e2b3f)',
    render: makeCardRender({ grad: ['#4a1220', '#8e2b3f'], accent: '#4a1220' }),
  },
  {
    id: 'charcoal-gradient',
    name: 'Antrasit Degrade',
    desc: 'Antrasit degrade · beyaz kart · isim + fiyat',
    swatch: 'linear-gradient(135deg,#2b2b2b,#5e5e5e)',
    render: makeCardRender({ grad: ['#2b2b2b', '#5e5e5e'], accent: '#2b2b2b' }),
  },
  {
    id: 'navy-installment',
    name: 'Lacivert · Taksit',
    desc: 'Lacivert degrade + vade farksız 9 taksit rozeti',
    swatch: 'linear-gradient(135deg,#12263a,#3a6073)',
    render: makeCardRender({
      grad: ['#12263a', '#3a6073'],
      accent: '#12263a',
      badge: {
        cx: W - 150, cy: 178, r: 108, rotate: 10,
        bg: '#e9b949', color: '#12263a', ring: 'rgba(0,0,0,0.18)',
        lines: [
          { t: 'VADE FARKSIZ', size: 22, weight: 700 },
          { t: '9', size: 66, weight: 800 },
          { t: 'TAKSİT', size: 30, weight: 800 },
        ],
      },
    }),
  },
  {
    id: 'promo-pillow-alez',
    name: 'Kampanya: Yastık & Alez',
    desc: "Üst başlık + sadece yastık/alez ürünleri",
    swatch: 'linear-gradient(135deg,#3a2233,#7d4a6b)',
    filter: 'pillow_alez',
    render: makeCardRender({
      grad: ['#3a2233', '#7d4a6b'],
      accent: '#3a2233',
      topText: ['Yastık ve alezlerde', "%30'a varan indirim"],
    }),
  },
  {
    id: 'collage-black',
    name: 'Kolaj (1+2)',
    desc: '1 büyük + 2 küçük görsel · siyah zemin',
    swatch: '#0c0c0c',
    multi: true,
    mode: 'collage',
    render: makeCollageRender({ bg: '#0c0c0c', promoText: { pre: "%50'YE VARAN", hi: 'İNDİRİM' } }),
  },
  {
    id: 'split-promo-navy',
    name: 'Split Kampanya',
    desc: 'Sol lacivert panel (isim+fiyat+%50) · sağ 2 kare görsel',
    swatch: 'linear-gradient(135deg,#12263a,#3a6073)',
    multi: true,
    mode: 'collage',
    render: makeSplitPromoRender({
      grad: ['#12263a', '#3a6073'],
      pct: 50,
      line1: '2. Ürüne Sepette Ekstra',
      suffix: 'İndirim',
    }),
  },
  {
    id: 'sale-badge',
    name: 'İndirim Rozeti',
    desc: 'Sıcak zemin · indirim % rozeti · fiyat',
    swatch: '#f3ede3',
    render(ctx, { img, logo, product }) {
      ctx.fillStyle = '#f3ede3';
      ctx.fillRect(0, 0, W, H);
      drawContain(ctx, img, 90, 80, W - 180, H - 250);

      // fiyat / indirim
      const price = product.price;
      const sale = product.salePrice;
      const hasDiscount = sale != null && price != null && sale < price;

      // alt koyu bant + logo + fiyat
      ctx.fillStyle = '#5e5e5e';
      ctx.fillRect(0, H - 150, W, 150);
      drawLogo(ctx, logo, 250, H - 75, 380);

      if (price != null) {
        ctx.textAlign = 'right';
        if (hasDiscount) {
          ctx.font = '600 32px Poppins, Arial, sans-serif';
          ctx.fillStyle = 'rgba(255,255,255,0.6)';
          const oldTxt = fmtPrice(price, product.priceCurrency);
          ctx.fillText(oldTxt, W - 60, H - 90);
          const ow = ctx.measureText(oldTxt).width;
          ctx.strokeStyle = 'rgba(255,255,255,0.6)';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(W - 60 - ow, H - 100);
          ctx.lineTo(W - 60, H - 100);
          ctx.stroke();
          ctx.font = '700 52px Poppins, Arial, sans-serif';
          ctx.fillStyle = '#ffffff';
          ctx.fillText(fmtPrice(sale, product.salePriceCurrency), W - 60, H - 40);
        } else {
          ctx.font = '700 54px Poppins, Arial, sans-serif';
          ctx.fillStyle = '#ffffff';
          ctx.fillText(fmtPrice(price, product.priceCurrency), W - 60, H - 55);
        }
        ctx.textAlign = 'left';
      }

      // indirim rozeti (sağ üst daire)
      if (hasDiscount) {
        const pct = Math.round((1 - sale / price) * 100);
        const bx = W - 170, by = 170, r = 100;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.25)';
        ctx.shadowBlur = 20;
        ctx.fillStyle = '#c0392b';
        ctx.beginPath();
        ctx.arc(bx, by, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.font = '800 56px Poppins, Arial, sans-serif';
        ctx.fillText(`%${pct}`, bx, by - 2);
        ctx.font = '600 26px Poppins, Arial, sans-serif';
        ctx.fillText('İNDİRİM', bx, by + 42);
        ctx.textAlign = 'left';
      }
    },
  },
  {
    id: 'editorial-split',
    name: 'Editoryal Şerit',
    desc: 'Üstte ürün · altta renkli şerit + başlık',
    swatch: '#5e5e5e',
    render(ctx, { img, logo, product }) {
      const splitY = Math.round(H * 0.74);
      // üst — açık zemin + ürün (cover)
      ctx.fillStyle = '#efece7';
      ctx.fillRect(0, 0, W, splitY);
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, W, splitY);
      ctx.clip();
      drawCover(ctx, img, 0, 0, W, splitY);
      ctx.restore();
      // alt renkli şerit
      ctx.fillStyle = '#5e5e5e';
      ctx.fillRect(0, splitY, W, H - splitY);
      // logo sol üstte şeritte
      drawLogo(ctx, logo, 210, splitY + 70, 300);
      // başlık sağda / altta
      ctx.fillStyle = '#ffffff';
      ctx.font = '600 40px Poppins, Arial, sans-serif';
      const lines = wrapLines(ctx, product.title, W - 120).slice(0, 2);
      let ty = splitY + 150;
      for (const ln of lines) {
        ctx.fillText(ln, 60, ty);
        ty += 52;
      }
      if (product.price != null) {
        ctx.font = '700 46px Poppins, Arial, sans-serif';
        ctx.fillStyle = '#ffe8a3';
        ctx.fillText(
          fmtPrice(product.salePrice != null ? product.salePrice : product.price, product.priceCurrency),
          60,
          H - 50
        );
      }
    },
  },
  {
    id: 'polaroid',
    name: 'Polaroid Kart',
    desc: 'Yumuşak zemin · beyaz kart · alt bant',
    swatch: '#e9e5df',
    render(ctx, { img, logo, product }) {
      ctx.fillStyle = '#e9e5df';
      ctx.fillRect(0, 0, W, H);
      const cx = 110, cy = 120, cw = W - 220, ch = H - 240;
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.22)';
      ctx.shadowBlur = 35;
      ctx.shadowOffsetY = 18;
      ctx.fillStyle = '#ffffff';
      roundRect(ctx, cx, cy, cw, ch, 24);
      ctx.fill();
      ctx.restore();
      // ürün — kartın üst kısmı
      const pad = 40;
      const imgH = ch - 150;
      ctx.save();
      roundRect(ctx, cx + pad, cy + pad, cw - pad * 2, imgH, 12);
      ctx.clip();
      drawCover(ctx, img, cx + pad, cy + pad, cw - pad * 2, imgH);
      ctx.restore();
      // alt koyu bant (kart içinde) + logo
      const barY = cy + pad + imgH + 20;
      const barH = ch - pad - imgH - 40;
      ctx.fillStyle = '#5e5e5e';
      roundRect(ctx, cx + pad, barY, cw - pad * 2, barH, 12);
      ctx.fill();
      drawLogo(ctx, logo, W / 2, barY + barH / 2, 360);
    },
  },
];

// ---------------------------------------------------------------------------
// Tek ürün + çerçeve canvas'ı
// ---------------------------------------------------------------------------
function FramedCard({ product, frame, logo }) {
  const canvasRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    // çoklu görsel (kolaj) ise tüm görselleri, değilse tek ana görseli yükle
    const urls = frame.multi
      ? (product.images || [product.imageLink]).slice(0, 3).map(proxied)
      : [proxied(product.imageLink)];

    Promise.allSettled(urls.map(loadImage)).then((results) => {
      if (cancelled) return;
      const imgs = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
      if (imgs.length === 0) { setStatus('error'); return; }
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, W, H);
      try {
        frame.render(ctx, { img: imgs[0], imgs, logo, product });
        setStatus('ready');
      } catch (e) {
        console.error('frame render error', e);
        setStatus('error');
      }
    });
    return () => { cancelled = true; };
  }, [product, frame, logo]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas || status !== 'ready') return;
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('İndirilemedi'); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `yatas_${frame.id}_${product.offerId || product.itemGroupId || 'urun'}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  return (
    <div className="group relative rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm">
      <div className="relative" style={{ aspectRatio: `${W}/${H}` }}>
        <canvas
          ref={canvasRef}
          width={W}
          height={H}
          className="w-full h-full block"
          style={{ display: status === 'ready' ? 'block' : 'none' }}
        />
        {status === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2 p-4 text-center">
            <ImageOff className="w-8 h-8" />
            <span className="text-xs">Görsel yüklenemedi</span>
          </div>
        )}
        {status === 'ready' && (
          <button
            onClick={download}
            className="absolute bottom-3 right-3 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/70 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur"
          >
            <Download className="w-4 h-4" /> İndir
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hafif canvas (chrome yok) — Meta önizlemede kullanılır
// ---------------------------------------------------------------------------
function PreviewCanvas({ product, frame, logo, className = '', fit = 'width' }) {
  const ref = useRef(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    const urls = frame.multi
      ? (product.images || [product.imageLink]).slice(0, 3).map(proxied)
      : [proxied(product.imageLink)];
    Promise.allSettled(urls.map(loadImage)).then((results) => {
      if (cancelled) return;
      const imgs = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
      if (!imgs.length) { setStatus('error'); return; }
      const canvas = ref.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, W, H);
      try { frame.render(ctx, { img: imgs[0], imgs, logo, product }); setStatus('ready'); }
      catch (e) { console.error(e); setStatus('error'); }
    });
    return () => { cancelled = true; };
  }, [product, frame, logo]);

  const sizeStyle =
    fit === 'height'
      ? { height: '100%', width: 'auto', maxWidth: '100%', margin: '0 auto' }
      : { width: '100%' };

  return (
    <canvas
      ref={ref}
      width={W}
      height={H}
      className={className}
      style={{ display: 'block', opacity: status === 'ready' ? 1 : 0.4, ...sizeStyle }}
    />
  );
}

// ---------------------------------------------------------------------------
// Meta Ads önizleme — seçilen çerçeve için Carousel + Story mockup
// ---------------------------------------------------------------------------
function MetaAdsPreview({ frame, items, logo }) {
  const [featured, setFeatured] = useState(0);
  if (!items || !items.length) {
    return <p className="text-sm text-gray-400">Bu çerçeve için ürün yok.</p>;
  }
  const story = items[Math.min(featured, items.length - 1)];

  const Avatar = ({ size }) => (
    <div
      className="rounded-full bg-gray-900 flex items-center justify-center overflow-hidden shrink-0"
      style={{ width: size, height: size }}
    >
      <img src={LOGO_URL} alt="" style={{ width: size * 0.72 }} crossOrigin="anonymous" />
    </div>
  );

  const tiles = Array.from({ length: 4 }, (_, i) => items[i % items.length]);
  const storySix = Array.from({ length: 6 }, (_, i) => items[i % items.length]);

  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-10">
      {/* CAROUSEL */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Instagram · Carousel</p>
        <div className="w-full max-w-[380px] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5">
            <Avatar size={34} />
            <div className="leading-tight">
              <p className="text-sm font-semibold text-gray-900">yatasbedding</p>
              <p className="text-[11px] text-gray-400">Sponsorlu</p>
            </div>
            <MoreHorizontal className="w-5 h-5 text-gray-400 ml-auto" />
          </div>
          <div className="flex overflow-x-auto snap-x snap-mandatory bg-white">
            {items.map((p, i) => (
              <div key={i} className="snap-center shrink-0 w-full">
                <PreviewCanvas product={p} frame={frame} logo={logo} />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 px-3 py-2.5">
            <Heart className="w-6 h-6 text-gray-800" />
            <MessageCircle className="w-6 h-6 text-gray-800" />
            <Send className="w-6 h-6 text-gray-800" />
            <Bookmark className="w-6 h-6 text-gray-800 ml-auto" />
          </div>
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-gray-100 bg-gray-50">
            <span className="text-sm font-semibold text-gray-900">Alışverişe Başla</span>
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </div>
          <div className="px-3 py-2.5 text-sm text-gray-700">
            <span className="font-semibold">yatasbedding</span>{' '}
            <span className="line-clamp-2">{items[0].title}</span>
          </div>
        </div>
      </div>

      {/* COLLECTION */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Facebook · Collection</p>
        <div className="w-full max-w-[380px] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5">
            <Avatar size={34} />
            <div className="leading-tight">
              <p className="text-sm font-semibold text-gray-900">yatasbedding</p>
              <p className="text-[11px] text-gray-400">Sponsorlu</p>
            </div>
            <MoreHorizontal className="w-5 h-5 text-gray-400 ml-auto" />
          </div>
          {/* hero kreatif (seçili çerçeve) */}
          <PreviewCanvas product={items[0]} frame={frame} logo={logo} />
          {/* ürün kataloğu döşemesi */}
          <div className="grid grid-cols-4 gap-0.5 bg-white">
            {tiles.map((p, i) => (
              <div key={i} className="aspect-square bg-gray-100 overflow-hidden">
                <img src={proxied(p.imageLink)} alt="" className="w-full h-full object-cover" crossOrigin="anonymous" />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-gray-100 bg-gray-50">
            <span className="text-sm font-semibold text-gray-900">Alışverişe Başla</span>
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </div>
        </div>
      </div>

      {/* STORY */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Instagram · Story</p>
        <div
          className="relative w-full max-w-[300px] rounded-2xl overflow-hidden bg-black shadow-sm"
          style={{ aspectRatio: '9 / 16' }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <PreviewCanvas product={story} frame={frame} logo={logo} className="w-full" />
          </div>
          {/* üst chrome */}
          <div className="absolute top-0 inset-x-0 p-3">
            <div className="flex gap-1 mb-2">
              {items.map((_, i) => (
                <div key={i} className={`h-0.5 flex-1 rounded-full ${i === featured ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Avatar size={28} />
              <span className="text-xs font-semibold text-white drop-shadow">yatasbedding</span>
              <span className="text-[11px] text-white/70">Sponsorlu</span>
              <X className="w-4 h-4 text-white ml-auto" />
            </div>
          </div>
          {/* alt CTA */}
          <div className="absolute bottom-0 inset-x-0 p-4 flex flex-col items-center gap-2">
            <ChevronUp className="w-5 h-5 text-white animate-bounce" />
            <div className="w-full text-center bg-white/95 rounded-full py-2 text-sm font-semibold text-gray-900">
              Alışverişe Başla
            </div>
          </div>
        </div>
        {/* story ürün seçici */}
        <div className="flex gap-2 mt-3 flex-wrap">
          {items.map((p, i) => (
            <button
              key={i}
              onClick={() => setFeatured(i)}
              className={`w-10 h-12 rounded-md overflow-hidden border-2 ${featured === i ? 'border-gray-900' : 'border-transparent'}`}
              title={p.title}
            >
              <img src={proxied(p.imageLink)} alt="" className="w-full h-full object-cover" crossOrigin="anonymous" />
            </button>
          ))}
        </div>
      </div>

      {/* STORY — 6 ürünlü */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Instagram · Story (6 ürün)</p>
        <div
          className="relative w-full max-w-[300px] rounded-2xl overflow-hidden shadow-sm"
          style={{ aspectRatio: '9 / 16', background: 'linear-gradient(160deg,#12263a,#3a6073)' }}
        >
          <div className="absolute inset-0 flex flex-col p-3">
            <div className="flex gap-1 mb-2">
              <div className="h-0.5 flex-1 rounded-full bg-white" />
            </div>
            <div className="flex items-center gap-2">
              <Avatar size={26} />
              <span className="text-xs font-semibold text-white drop-shadow">yatasbedding</span>
              <span className="text-[11px] text-white/70">Sponsorlu</span>
              <X className="w-4 h-4 text-white ml-auto" />
            </div>
            <div className="grid grid-cols-2 grid-rows-3 gap-1.5 flex-1 min-h-0 mt-2">
              {storySix.map((p, i) => (
                <div key={i} className="flex items-center justify-center min-h-0 overflow-hidden rounded-md">
                  <PreviewCanvas product={p} frame={frame} logo={logo} fit="height" />
                </div>
              ))}
            </div>
            <div className="pt-2">
              <div className="w-full text-center bg-white/95 rounded-full py-2 text-sm font-semibold text-gray-900">
                Alışverişe Başla
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ana sayfa
// ---------------------------------------------------------------------------
export default function YatasFrameStudio() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id;

  // 3 ayrı ürün seti: standart çerçeveler / kampanya (yastık-alez) / kolaj
  const [sets, setSets] = useState({ general: [], promo: [], collage: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logo, setLogo] = useState(null);
  const [previewFrameId, setPreviewFrameId] = useState(FRAMES[2].id); // varsayılan: Lacivert Degrade

  // logoyu bir kez yükle
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setLogo(img);
    img.onerror = () => console.warn('logo yüklenemedi');
    img.src = LOGO_URL;
  }, []);

  const loadAll = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    const fetchSet = async (extra) => {
      const res = await fetch(
        `${API}/api/yatas-frame-studio/random-products?count=5${extra}`,
        { credentials: 'include', headers: { 'X-Brand-ID': brandId } }
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `Hata (${res.status})`);
      }
      const j = await res.json();
      return j.products || [];
    };
    try {
      const [general, promo, collage] = await Promise.all([
        fetchSet(''),
        fetchSet('&category=pillow_alez'),
        fetchSet('&collage=1'),
      ]);
      setSets({ general, promo, collage });
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // her çerçeve için doğru ürün setini seç
  const setFor = (f) =>
    f.mode === 'collage' ? sets.collage : f.filter === 'pillow_alez' ? sets.promo : sets.general;

  const hasAny = sets.general.length || sets.promo.length || sets.collage.length;

  const previewFrame = FRAMES.find((f) => f.id === previewFrameId) || FRAMES[0];
  const previewItems = setFor(previewFrame);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 sticky top-0 z-10 bg-white/90 backdrop-blur py-3 -mx-4 px-4 border-b border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Çerçeve Stüdyosu</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {selectedBrand?.name || 'Yataş Grubu'} — tüm çerçeveler alt alta · her görselin üzerine gelip indir
          </p>
        </div>
        <button
          onClick={loadAll}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shuffle className="w-4 h-4" />}
          Ürünleri Değiştir
        </button>
      </div>

      {error && !loading && (
        <div className="text-center py-16 text-gray-400">{error}</div>
      )}

      {loading && !hasAny ? (
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : (
        <div className="space-y-10">
          {FRAMES.map((f) => {
            const items = setFor(f);
            return (
              <section key={f.id}>
                <div className="flex items-baseline gap-3 mb-3">
                  <span
                    className="w-5 h-5 rounded-md border border-black/10 shrink-0"
                    style={{ background: f.swatch }}
                  />
                  <h2 className="text-lg font-bold text-gray-900">{f.name}</h2>
                  <span className="text-xs text-gray-400">{f.desc}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {items.map((p, i) => (
                    <FramedCard
                      key={`${f.id}-${p.offerId || p.itemGroupId || i}`}
                      product={p}
                      frame={f}
                      logo={logo}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {!loading && !hasAny && !error && (
        <div className="text-center py-16 text-gray-400">Ürün bulunamadı.</div>
      )}

      {/* Meta Ads Önizleme — seçilen çerçeve için */}
      {hasAny && (
        <div className="mt-16 pt-8 border-t border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-1">Meta Ads Önizleme</h2>
          <p className="text-sm text-gray-500 mb-4">
            Bir çerçeve seç — carousel ve story'de nasıl görüneceğini gör.
          </p>
          <div className="flex flex-wrap gap-2 mb-6">
            {FRAMES.map((f) => (
              <button
                key={f.id}
                onClick={() => setPreviewFrameId(f.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                  previewFrameId === f.id
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-200 text-gray-700 hover:border-gray-300 bg-white'
                }`}
              >
                <span className="w-3 h-3 rounded-sm border border-white/20" style={{ background: f.swatch }} />
                {f.name}
              </button>
            ))}
          </div>
          <MetaAdsPreview frame={previewFrame} items={previewItems} logo={logo} />
        </div>
      )}
    </div>
  );
}
